import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueries } from '@tanstack/react-query';
import { gardensService, swatidService } from '@/services';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { Garden, User } from '@/types';
import type { ApiController, ApiSensor } from '@/types/api';
import { Loader2, MapPin, Plus, Trash2, Layers, Cpu, Thermometer, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSwatidRelayDisplayName, isDefaultSwatidRelayName } from '@/utils/swatidRelay';

const gardenSchema = z.object({
  // AJUSTE: name max 255 según documentación validada del backend
  name: z.string().min(1, 'El nombre es requerido').max(255, 'El nombre no puede exceder 255 caracteres'),
  description: z.string().max(500).optional(),
  companyId: z.string().min(1, 'La empresa es requerida'),
  municipality: z.string().min(1, 'El municipio es requerido').max(100),
  lat: z.coerce.number().min(-90, 'La latitud debe estar entre -90 y 90').max(90, 'La latitud debe estar entre -90 y 90'),
  lng: z.coerce.number().min(-180, 'La longitud debe estar entre -180 y 180').max(180, 'La longitud debe estar entre -180 y 180'),
  area: z.coerce.number().min(0, 'El área debe ser un número positivo').optional(),
});

type GardenFormData = z.infer<typeof gardenSchema>;

// Custom native select component for better compatibility (igual que en ControllerFormDialog)
interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer",
          !value && "text-muted-foreground",
          className
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
}

/**
 * Estructura de zona en el formulario
 * - sensorId: a nivel de zona (0 o 1 sensor)
 * - controllerAssignments: array de controlador+válvula
 */
interface ZoneFormData {
  id: string;
  name: string;
  sensorId?: string;
  controllerAssignments: {
    id: string;
    controllerId: string;
    valveChannel: number;
  }[];
}

interface GardenFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garden?: Garden;
  companies?: { id: string; name: string }[];
  controllers?: ApiController[];
  sensors?: ApiSensor[];
  onSubmit: (data: Partial<Garden>) => void;
  isLoading?: boolean;
  currentUser?: User | null;
  isSuperAdmin?: boolean;
}

export function GardenFormDialog({
  open,
  onOpenChange,
  garden,
  companies = [],
  controllers = [],
  sensors = [],
  onSubmit,
  isLoading = false,
  currentUser,
  isSuperAdmin = false,
}: GardenFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zones, setZones] = useState<ZoneFormData[]>([]);
  const isEditing = !!garden;
  
  // Si el usuario es company_admin, solo mostrar su empresa
  const availableCompanies = isSuperAdmin
    ? companies
    : currentUser?.companyId
    ? companies.filter(c => c.id === currentUser.companyId)
    : companies;
  
  // Preseleccionar companyId si es company_admin y no está editando
  const defaultCompanyId = !isEditing && !isSuperAdmin && currentUser?.companyId
    ? currentUser.companyId
    : garden?.companyId || '';

  // Debug: verificar que los datos lleguen
  useEffect(() => {
    if (open) {
      console.log('[GardenFormDialog] Companies:', companies.length, companies);
      console.log('[GardenFormDialog] Controllers:', controllers.length, controllers);
      console.log('[GardenFormDialog] Sensors:', sensors.length, sensors);
    }
  }, [open, companies, controllers, sensors]);

  // Inicializar form PRIMERO para evitar problemas de TDZ
  const form = useForm<GardenFormData>({
    resolver: zodResolver(gardenSchema),
    defaultValues: {
      name: garden?.name || '',
      description: garden?.description || '',
      companyId: defaultCompanyId,
      municipality: garden?.municipality || '',
      lat: garden?.coordinates?.lat || 39.47,
      lng: garden?.coordinates?.lng || -0.38,
      area: garden?.area || undefined,
    },
  });

  // Obtener el companyId seleccionado del formulario
  const selectedCompanyId = form.watch('companyId');

  // CAMBIO v0.2.0: Filtrar controllers y sensors por empresa seleccionada
  // Los datos vienen como props desde la página padre (igual que en Controllers y Sensors)
  const availableControllers = selectedCompanyId
    ? controllers.filter(c => c.companyId === selectedCompanyId)
    : controllers;

  const availableSensors = selectedCompanyId
    ? sensors.filter(s => s.companyId === selectedCompanyId)
    : sensors;

  const swatidControllerIds = availableControllers
    .filter((c) => c.model === 'SWATID_16')
    .map((c) => c.id);

  const swatidRelayQueries = useQueries({
    queries: swatidControllerIds.map((id) => ({
      queryKey: ['swatid-relays', id],
      queryFn: async () => {
        const res = await swatidService.getRelays(id);
        if (!res.success) return [];
        return res.data ?? [];
      },
      enabled: open && !!id,
      staleTime: 30_000,
    })),
  });

  const swatidRelayLabelByController = new Map<string, Map<number, string>>();
  swatidControllerIds.forEach((id, index) => {
    const relays = swatidRelayQueries[index]?.data ?? [];
    const labels = new Map<number, string>();
    relays.forEach((r) => labels.set(r.relayNumber, r.name?.trim() || getSwatidRelayDisplayName(r)));
    swatidRelayLabelByController.set(id, labels);
  });

  // Cargar zonas desde el backend cuando se edita un jardín
  const { data: zonesData } = useQuery({
    queryKey: ['garden-zones-form', garden?.id],
    queryFn: async () => {
      if (!garden?.id) return [];
      const response = await gardensService.getZones(garden.id);
      if (!response.success) {
        console.error('Error al cargar zonas:', response.error);
        return [];
      }
      return response.data?.items || [];
    },
    enabled: open && !!garden?.id,
  });

  useEffect(() => {
    if (garden) {
      form.reset({
        name: garden.name,
        description: garden.description || '',
        companyId: garden.companyId,
        municipality: garden.municipality,
        lat: garden.coordinates?.lat || 39.47,
        lng: garden.coordinates?.lng || -0.38,
        area: garden.area || undefined,
      });
    } else {
      // Al crear un nuevo jardín, preseleccionar companyId si es company_admin
      const initialCompanyId = !isSuperAdmin && currentUser?.companyId
        ? currentUser.companyId
        : '';
      form.reset({
        name: '',
        description: '',
        companyId: initialCompanyId,
        municipality: '',
        lat: 39.47,
        lng: -0.38,
        area: undefined,
      });
      setZones([]);
    }
  }, [garden, form, open, currentUser, isSuperAdmin]);

  // Cargar zonas cuando se obtienen del backend
  useEffect(() => {
    if (zonesData && zonesData.length > 0) {
      // Convertir ApiZone a ZoneFormData
      const gardenZones: ZoneFormData[] = zonesData.map((z) => ({
        id: z.id,
        name: z.name,
        sensorId: z.sensorId || undefined,
        controllerAssignments: z.controllerAssignments?.map(ca => ({
          id: ca.id,
          controllerId: ca.controllerId,
          valveChannel: ca.valveChannel,
        })) || [],
      }));
      setZones(gardenZones);
    } else if (garden && !zonesData) {
      // Si estamos editando pero no hay zonas, limpiar
      setZones([]);
    }
  }, [zonesData, garden]);

  const handleAddZone = () => {
    const newZone: ZoneFormData = {
      id: `zone-temp-${Date.now()}`,
      name: '',
      sensorId: undefined,
      controllerAssignments: [],
    };
    setZones((prev) => [...prev, newZone]);
  };

  const handleRemoveZone = (zoneId: string) => {
    setZones((prev) => prev.filter((z) => z.id !== zoneId));
  };

  const handleZoneNameChange = (zoneId: string, name: string) => {
    setZones((prev) =>
      prev.map((z) => (z.id === zoneId ? { ...z, name } : z))
    );
  };

  const handleZoneSensorChange = (zoneId: string, sensorId: string | undefined) => {
    setZones((prev) =>
      prev.map((z) => (z.id === zoneId ? { ...z, sensorId } : z))
    );
  };

  const handleAddControllerAssignment = (zoneId: string) => {
    const newAssignment = {
      id: `ca-temp-${Date.now()}`,
      controllerId: '',
      valveChannel: 1,
    };
    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? { ...z, controllerAssignments: [...(z.controllerAssignments || []), newAssignment] }
          : z
      )
    );
  };

  const handleRemoveControllerAssignment = (zoneId: string, assignmentId: string) => {
    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? {
              ...z,
              controllerAssignments: (z.controllerAssignments || []).filter(
                (ca) => ca.id !== assignmentId
              ),
            }
          : z
      )
    );
  };

  const handleControllerAssignmentChange = (
    zoneId: string,
    assignmentId: string,
    field: 'controllerId' | 'valveChannel',
    value: string | number
  ) => {
    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? {
              ...z,
              controllerAssignments: (z.controllerAssignments || []).map((ca) =>
                ca.id === assignmentId
                  ? { ...ca, [field]: value }
                  : ca
              ),
            }
          : z
      )
    );
  };

  // Check if a controller+valve combination is already used in any zone
  const isControllerValveUsed = (
    controllerId: string,
    valveChannel: number,
    excludeAssignmentId?: string
  ) => {
    return zones.some(
      (z) =>
        z.controllerAssignments.some(
          (ca) =>
            ca.id !== excludeAssignmentId &&
            ca.controllerId === controllerId &&
            ca.valveChannel === valveChannel
        )
    );
  };

  // Check if a sensor is already used in another zone
  const isSensorUsed = (sensorId: string, excludeZoneId?: string) => {
    return zones.some((z) => z.id !== excludeZoneId && z.sensorId === sensorId);
  };

  const getControllerValves = (controllerId: string) => {
    const controller = availableControllers.find((c) => c.id === controllerId);
    return controller?.valves || [];
  };

  const getControllerModel = (controllerId: string) => {
    const controller = availableControllers.find((c) => c.id === controllerId);
    return controller?.model;
  };

  const getRelayOrValveLabel = (controllerId: string, channel: number): string | undefined => {
    if (getControllerModel(controllerId) === 'SWATID_16') {
      return swatidRelayLabelByController.get(controllerId)?.get(channel);
    }
    const valve = getControllerValves(controllerId).find((v) => v.channel === channel);
    return valve?.name;
  };

  const formatChannelLabel = (
    controllerId: string,
    channel: number,
    isSwat: boolean,
    isUsed: boolean
  ): string => {
    const customLabel = getRelayOrValveLabel(controllerId, channel);
    const base = isSwat ? `Relé ${channel}` : `Válvula ${channel}`;
    const showSuffix =
      customLabel &&
      (isSwat
        ? !isDefaultSwatidRelayName(channel, customLabel)
        : true);
    return `${base}${showSuffix ? ` - ${customLabel}` : ''}${isUsed ? ' (en uso)' : ''}`;
  };

  const handleSubmit = async (data: GardenFormData) => {
    setIsSubmitting(true);
    try {
      // Validar que las zonas tengan nombres
      const invalidZones = zones.filter((z) => !z.name.trim());
      if (invalidZones.length > 0) {
        setIsSubmitting(false);
        return;
      }

      // Validar que las zonas tengan al menos una asignación de controlador/válvula
      const zonesWithoutAssignments = zones.filter(
        (z) => z.controllerAssignments.length === 0 || 
               z.controllerAssignments.every(ca => !ca.controllerId)
      );
      if (zonesWithoutAssignments.length > 0) {
        // No bloquear, pero advertir en la UI si es necesario
        console.warn('Algunas zonas no tienen controladores asignados');
      }

      const gardenData: Partial<Garden> = {
        name: data.name,
        description: data.description || undefined,
        companyId: data.companyId,
        municipality: data.municipality,
        coordinates: {
          lat: data.lat,
          lng: data.lng,
        },
        area: data.area || undefined,
        // Las zonas se gestionan aparte vía API después de crear/actualizar el jardín
        zones: zones.map((z) => ({
          id: z.id,
          name: z.name,
          gardenId: garden?.id || '',
          sensorId: z.sensorId || undefined, // Sensor a nivel de zona
          // Convertir a la estructura esperada por el frontend
          controllerAssignments: z.controllerAssignments
            .filter(ca => ca.controllerId)
            .map(ca => ({
              id: ca.id,
              controllerId: ca.controllerId,
              valveChannel: ca.valveChannel as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16,
            })),
          scheduleAssignments: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        })),
      };

      if (isEditing && garden) {
        gardenData.id = garden.id;
      }

      onSubmit(gardenData);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoadingData = isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[95vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
          <DialogTitle className="font-display">
            {isEditing ? 'Editar Jardín' : 'Nuevo Jardín'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica la información del jardín y sus zonas.'
              : 'Añade un nuevo jardín y configura sus zonas de riego.'}
          </DialogDescription>
        </DialogHeader>

        {isLoadingData ? (
          <div className="space-y-4 px-6 py-6 overflow-y-auto">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <Form {...form}>
                <form id="garden-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre *</FormLabel>
                        <FormControl>
                          <Input placeholder="Parque del Retiro - Zona Norte" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descripción</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Descripción del jardín..."
                            className="resize-none"
                            rows={2}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="companyId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Empresa *</FormLabel>
                          <FormControl>
                            <NativeSelect
                              value={field.value || ''}
                              onChange={field.onChange}
                              placeholder="Seleccionar empresa"
                              disabled={!isSuperAdmin && !!currentUser?.companyId}
                              options={
                                availableCompanies.length === 0
                                  ? [{ value: '', label: 'No hay empresas disponibles' }]
                                  : availableCompanies.map((c) => ({ value: c.id, label: c.name }))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="municipality"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Municipio *</FormLabel>
                          <FormControl>
                            <Input placeholder="Valencia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Coordenadas *
                    </FormLabel>
                    <div className="grid grid-cols-3 gap-3">
                      <FormField
                        control={form.control}
                        name="lat"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                step="any"
                                placeholder="Latitud"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lng"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                step="any"
                                placeholder="Longitud"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="area"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Área (m²)"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Zones Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Layers className="w-4 h-4 text-primary" />
                        Zonas de Riego
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Cada zona debe tener al menos 1 controlador+válvula y puede tener 0-1 sensor
                      </p>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Badge variant="secondary">
                        {zones.length} zona{zones.length !== 1 ? 's' : ''}
                      </Badge>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddZone}
                        className="gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Añadir Zona
                      </Button>
                    </div>
                  </div>

                  {zones.length === 0 ? (
                    <div className="p-6 rounded-lg bg-muted/50 text-center">
                      <Layers className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No hay zonas configuradas
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Añade zonas para organizar el riego de tu jardín
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {zones.map((zone, index) => (
                        <div
                          key={zone.id}
                          className="p-4 rounded-lg border bg-card space-y-4"
                        >
                          {/* Zone Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 space-y-3">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  Zona {index + 1}
                                </Badge>
                              </div>
                              <Input
                                placeholder="Nombre de la zona (ej: Césped Principal)"
                                value={zone.name}
                                onChange={(e) =>
                                  handleZoneNameChange(zone.id, e.target.value)
                                }
                                className={!zone.name.trim() ? 'border-destructive' : ''}
                              />
                              {!zone.name.trim() && (
                                <p className="text-xs text-destructive">
                                  El nombre es requerido
                                </p>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveZone(zone.id)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Sensor Assignment (a nivel de zona) */}
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                            <Thermometer className="w-4 h-4 text-info" />
                            <span className="text-sm font-medium">Sensor:</span>
                            <div className="flex-1 max-w-[250px]">
                              <NativeSelect
                                value={zone.sensorId || 'none'}
                                onChange={(value) =>
                                  handleZoneSensorChange(zone.id, value === 'none' ? undefined : value)
                                }
                                placeholder="Sin sensor"
                                className="h-8 text-xs"
                                options={[
                                  { value: 'none', label: 'Sin sensor' },
                                  ...availableSensors.map((sensor) => {
                                    const isUsed = isSensorUsed(sensor.id, zone.id);
                                    return {
                                      value: sensor.id,
                                      label: `${sensor.name}${isUsed ? ' (asignado a otra zona)' : ''}`,
                                    };
                                  }),
                                ]}
                              />
                            </div>
                          </div>

                          {/* Controller Assignments */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-muted-foreground" />
                                Controladores y Válvulas *
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddControllerAssignment(zone.id)}
                                className="gap-1 h-7 text-xs"
                              >
                                <Plus className="w-3 h-3" />
                                Añadir
                              </Button>
                            </div>

                            {!zone.controllerAssignments || zone.controllerAssignments.length === 0 ? (
                              <div className="p-3 rounded border border-dashed border-warning/50 bg-warning/5 text-center">
                                <p className="text-xs text-warning">
                                  ⚠️ Una zona debe tener al menos 1 controlador+válvula
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {(zone.controllerAssignments || []).map((assignment) => (
                                  <div
                                    key={assignment.id}
                                    className="p-3 rounded-lg bg-muted/50 flex items-center gap-2"
                                  >
                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                      {/* Controller Select */}
                                      <NativeSelect
                                        value={assignment.controllerId || 'none'}
                                        onChange={(value) =>
                                          handleControllerAssignmentChange(
                                            zone.id,
                                            assignment.id,
                                            'controllerId',
                                            value === 'none' ? '' : value
                                          )
                                        }
                                        placeholder="Controlador"
                                        className="h-8 text-xs"
                                        options={[
                                          { value: 'none', label: 'Seleccionar...' },
                                          ...(availableControllers.length === 0
                                            ? [{ value: 'no-controllers', label: 'No hay controladoras disponibles' }]
                                            : availableControllers.map((controller) => ({
                                                value: controller.id,
                                                label: `${controller.name} (${controller.model})`,
                                              }))),
                                        ]}
                                      />

                                      {/* Valve Select */}
                                      <NativeSelect
                                        value={String(assignment.valveChannel)}
                                        onChange={(value) =>
                                          handleControllerAssignmentChange(
                                            zone.id,
                                            assignment.id,
                                            'valveChannel',
                                            parseInt(value)
                                          )
                                        }
                                        disabled={!assignment.controllerId}
                                        placeholder="Válvula"
                                        className="h-8 text-xs"
                                        options={(() => {
                                          const model = getControllerModel(assignment.controllerId);
                                          const isSwat = model === 'SWATID_16';
                                          const maxChannels = isSwat ? 16 : 2;
                                          
                                          return Array.from({ length: maxChannels }, (_, i) => i + 1).map((channel) => {
                                            const isUsed = isControllerValveUsed(
                                              assignment.controllerId,
                                              channel,
                                              assignment.id
                                            );
                                            const label = formatChannelLabel(
                                              assignment.controllerId,
                                              channel,
                                              isSwat,
                                              isUsed
                                            );
                                            return {
                                              value: String(channel),
                                              label,
                                            };
                                          });
                                        })()}
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        handleRemoveControllerAssignment(zone.id, assignment.id)
                                      }
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                </form>
              </Form>
            </div>
            <DialogFooter className="px-6 py-4 border-t bg-background flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="garden-form"
                disabled={isSubmitting || isLoading}
                className="gradient-primary text-white"
                onClick={(e) => {
                  e.preventDefault();
                  form.handleSubmit(handleSubmit)();
                }}
              >
                {(isSubmitting || isLoading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Guardar cambios' : 'Crear jardín'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
